
import React, { useState } from 'react';
import { Zap, Link2, Activity, ShieldCheck, Server, Loader2, RefreshCw, Terminal } from 'lucide-react';
import { Card, Badge } from './SharedComponents';
import { GoogleGenAI } from "@google/genai";

export const IntegrationCore = () => {
  const [iaStatus, setIaStatus] = useState<'IDLE' | 'TESTING' | 'ONLINE' | 'ERROR'>('IDLE');
  const [lastCheck, setLastCheck] = useState<string | null>(null);
  const [latency, setLatency] = useState<number | null>(null);

  const testIAConnection = async () => {
    setIaStatus('TESTING');
    const start = Date.now();
    
    try {
      // Inicializa com a chave do ambiente (process.env.API_KEY injetado)
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: 'ping',
      });

      if (response.text) {
        setLatency(Date.now() - start);
        setIaStatus('ONLINE');
        setLastCheck(new Date().toLocaleTimeString());
      } else {
        throw new Error("Resposta vazia");
      }
    } catch (error) {
      console.error("Erro no Core IA:", error);
      setIaStatus('ERROR');
    }
  };

  return (
    <div className="p-10 space-y-10 animate-in fade-in duration-700 min-h-screen text-secondary bg-[#fcfdfe]">
      <div className="flex justify-between items-end">
        <div>
          <div className="flex items-center gap-2 text-primary font-black uppercase text-[10px] tracking-[0.4em] mb-3">
            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div>
            Infraestructure Management
          </div>
          <h1 className="text-4xl font-black uppercase tracking-tighter">API & Core Engine</h1>
        </div>
        
        <div className="flex items-center gap-4">
           <Badge className="bg-green-50 text-green-600 border-none px-4 py-2 font-black text-[10px]">SISTEMA OPERACIONAL</Badge>
           <div className="h-10 w-[1px] bg-gray-100 mx-2"></div>
           <div className="text-right">
              <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Uptime Global</p>
              <p className="text-sm font-black text-secondary">99.98%</p>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Card Gemini Vision AI - Funcional */}
        <Card className="p-10 bg-white border border-gray-100 shadow-2xl space-y-8 rounded-[48px] relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:rotate-12 transition-transform duration-1000">
             <Link2 size={180} />
          </div>

          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-5">
              <div className={`w-16 h-16 rounded-[24px] flex items-center justify-center transition-all ${
                iaStatus === 'ONLINE' ? 'bg-green-500 text-white shadow-lg shadow-green-200' : 
                iaStatus === 'ERROR' ? 'bg-red-500 text-white' : 'bg-primary/10 text-primary'
              }`}>
                {iaStatus === 'TESTING' ? <Loader2 size={32} className="animate-spin" /> : <Zap size={32} />}
              </div>
              <div>
                <h3 className="text-2xl font-black uppercase text-secondary tracking-tight leading-none">Gemini Vision AI</h3>
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mt-2">Motor de Análise de Avarias</p>
              </div>
            </div>
            {iaStatus === 'ONLINE' && (
              <div className="text-right animate-in fade-in">
                 <p className="text-[10px] font-black text-green-500 uppercase tracking-widest">Latência: {latency}ms</p>
                 <p className="text-[8px] font-bold text-gray-300 uppercase">Check: {lastCheck}</p>
              </div>
            )}
          </div>

          <div className="space-y-6 relative z-10">
             <div className="bg-gray-50 p-6 rounded-[32px] border border-gray-100 flex items-center justify-between shadow-inner">
                <div className="flex items-center gap-3">
                   <Terminal size={16} className="text-gray-400" />
                   <span className="text-xs font-mono font-bold text-gray-400">Vault: API_KEY_CORE_PROD</span>
                </div>
                <button 
                  onClick={testIAConnection}
                  disabled={iaStatus === 'TESTING'}
                  className="bg-white border border-gray-200 text-secondary px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-primary hover:text-white hover:border-primary transition-all shadow-sm active:scale-95 disabled:opacity-50"
                >
                   Verificar Conexão
                </button>
             </div>
             
             <div className="flex items-start gap-4">
                <div className={`p-2 rounded-xl shrink-0 ${iaStatus === 'ERROR' ? 'bg-red-50 text-red-500' : 'bg-primary/5 text-primary'}`}>
                   <ShieldCheck size={20} />
                </div>
                <p className="text-xs text-gray-500 leading-relaxed font-medium">
                  Status: {iaStatus === 'ONLINE' ? 'Conexão segura estabelecida com Google Cloud Vertex AI.' : 
                          iaStatus === 'ERROR' ? 'Falha na autenticação. Verifique as variáveis de ambiente.' :
                          'As chaves de IA são carregadas via variáveis de ambiente (.env) para máxima segurança do core.'}
                </p>
             </div>
          </div>
        </Card>

        {/* Status Webhooks - Funcional Visual */}
        <Card className="p-10 bg-white border border-gray-100 shadow-2xl space-y-10 rounded-[48px] relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] group-hover:scale-110 transition-transform duration-1000">
             <Activity size={180} />
          </div>

          <div className="flex items-center gap-5 relative z-10">
            <div className="w-16 h-16 bg-green-500/10 text-green-600 rounded-[24px] flex items-center justify-center shadow-inner">
               <Activity size={32} />
            </div>
            <div>
              <h3 className="text-2xl font-black uppercase text-secondary tracking-tight leading-none">Webhooks SaaS</h3>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] mt-2">Sincronização de Pagamentos (Stripe/MP)</p>
            </div>
          </div>

          <div className="space-y-3 relative z-10">
             <WebhookRow label="infra.stripe.checkout.completed" active status="LISTENING" />
             <WebhookRow label="infra.mp.payment.created" active status="LISTENING" />
             <WebhookRow label="core.saas.subscription.renewed" active status="HEALTHY" />
             <WebhookRow label="core.audit.safety_check" active status="HEALTHY" />
          </div>

          <div className="pt-4 border-t border-gray-50 flex justify-between items-center relative z-10">
             <div className="flex items-center gap-2">
                <RefreshCw size={14} className="text-primary animate-spin-slow" />
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Escuta ativa em tempo real</span>
             </div>
             <button className="text-primary text-[10px] font-black uppercase tracking-widest hover:underline">Ver logs de tráfego</button>
          </div>
        </Card>
      </div>

      {/* Seção de Nodes da Infraestrutura */}
      <Card className="p-10 bg-secondary text-white border-none shadow-2xl rounded-[48px] relative overflow-hidden">
         <div className="absolute top-0 right-0 p-10 opacity-5">
            <Server size={140} />
         </div>
         <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="space-y-2 text-center md:text-left">
               <h4 className="text-xl font-black uppercase tracking-tight italic">Node Cluster Health</h4>
               <p className="text-white/40 text-[10px] font-bold uppercase tracking-[0.4em]">Redundância de Servidores EventLog CORE</p>
            </div>
            <div className="flex gap-4">
               <NodeIndicator name="SA-EAST-1" status="Primary" active />
               <NodeIndicator name="US-EAST-1" status="Failover" active />
               <NodeIndicator name="EU-WEST-1" status="Backup" active />
            </div>
         </div>
      </Card>
    </div>
  );
};

const WebhookRow = ({ label, active, status }: any) => (
  <div className="flex items-center justify-between p-5 bg-gray-50 rounded-2xl border border-gray-100 hover:bg-white hover:shadow-lg transition-all group">
    <div className="flex items-center gap-3">
       <div className={`w-2 h-2 rounded-full ${active ? 'bg-green-500 animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.5)]' : 'bg-red-500'}`}></div>
       <span className="text-[11px] font-mono font-bold text-secondary group-hover:text-primary transition-colors">{label}</span>
    </div>
    <Badge className="bg-white text-gray-400 border-gray-200 text-[8px] font-black">{status}</Badge>
  </div>
);

const NodeIndicator = ({ name, status, active }: any) => (
  <div className="bg-white/5 border border-white/10 px-6 py-4 rounded-3xl flex flex-col items-center gap-1 min-w-[120px]">
     <p className="text-[10px] font-black text-white">{name}</p>
     <div className="flex items-center gap-1.5">
        <div className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-primary animate-pulse' : 'bg-gray-500'}`}></div>
        <span className="text-[8px] font-bold text-white/40 uppercase">{status}</span>
     </div>
  </div>
);
